import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { 
  SparklesIcon, 
  CpuChipIcon, 
  ShareIcon, 
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  PlayIcon
} from '@heroicons/react/24/outline'

// 3D 背景动画组件
function StarField() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }
    resize()
    window.addEventListener('resize', resize)

    // 星星粒子
    const stars: { x: number; y: number; z: number; size: number; color: string }[] = []
    const colors = ['#00f0ff', '#9d00ff', '#ff00a0', '#ffffff']
    
    for (let i = 0; i < 300; i++) {
      stars.push({
        x: Math.random() * canvas.width - canvas.width / 2,
        y: Math.random() * canvas.height - canvas.height / 2,
        z: Math.random() * 1500,
        size: Math.random() * 2 + 0.5,
        color: colors[Math.floor(Math.random() * colors.length)],
      })
    }

    let animationId: number
    let mouseX = 0
    let mouseY = 0

    const handleMouseMove = (e: MouseEvent) => {
      mouseX = (e.clientX - canvas.width / 2) * 0.001
      mouseY = (e.clientY - canvas.height / 2) * 0.001
    }
    window.addEventListener('mousemove', handleMouseMove)

    const animate = () => {
      // 渐变背景
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, canvas.width
      )
      gradient.addColorStop(0, '#0a0a0f')
      gradient.addColorStop(0.5, '#0f0f1a')
      gradient.addColorStop(1, '#0a0a0f')
      ctx.fillStyle = gradient
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      const centerX = canvas.width / 2
      const centerY = canvas.height / 2

      stars.forEach((star) => {
        star.z -= 1
        if (star.z <= 0) {
          star.z = 1500
          star.x = Math.random() * canvas.width - centerX
          star.y = Math.random() * canvas.height - centerY
        }

        const scale = 1500 / (1500 - star.z)
        const x = centerX + star.x * scale + mouseX * star.z * 0.1
        const y = centerY + star.y * scale + mouseY * star.z * 0.1
        const size = star.size * scale * 0.3

        const alpha = Math.min(1, (1500 - star.z) / 800)

        // 发光效果
        const glowSize = size * 2
        const glowGradient = ctx.createRadialGradient(x, y, 0, x, y, glowSize)
        glowGradient.addColorStop(0, star.color + Math.floor(alpha * 50).toString(16).padStart(2, '0'))
        glowGradient.addColorStop(1, 'transparent')
        
        ctx.beginPath()
        ctx.arc(x, y, glowSize, 0, Math.PI * 2)
        ctx.fillStyle = glowGradient
        ctx.fill()

        // 星星本体
        ctx.beginPath()
        ctx.arc(x, y, size, 0, Math.PI * 2)
        ctx.fillStyle = star.color + Math.floor(alpha * 255).toString(16).padStart(2, '0')
        ctx.fill()
      })

      animationId = requestAnimationFrame(animate)
    }

    animate()

    return () => {
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', handleMouseMove)
      cancelAnimationFrame(animationId)
    }
  }, [])

  return <canvas ref={canvasRef} className="absolute inset-0 -z-10" />
}

// 特性卡片
function FeatureCard({
  icon: Icon,
  title,
  description,
  gradient,
}: {
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  gradient: string
}) {
  return (
    <div className="group relative">
      {/* 发光边框 */}
      <div className={`absolute -inset-0.5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur ${gradient}`} />
      
      <div className="relative cyber-card p-6 h-full group-hover:border-cyber-blue/50 transition-all duration-500">
        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradient} p-0.5 mb-5`}>
          <div className="w-full h-full rounded-xl bg-cyber-dark flex items-center justify-center">
            <Icon className="w-7 h-7 text-white" />
          </div>
        </div>
        <h3 className="text-lg font-semibold mb-2 text-white font-cyber">{title}</h3>
        <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  )
}

// 流程步骤
function ProcessStep({
  step,
  title,
  desc,
  isLast,
}: {
  step: string
  title: string
  desc: string
  isLast?: boolean
}) {
  return (
    <div className="relative flex gap-6">
      {/* 左侧步骤号 */}
      <div className="flex flex-col items-center">
        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-cyber-blue to-cyber-purple p-0.5">
          <div className="w-full h-full rounded-xl bg-cyber-dark flex items-center justify-center">
            <span className="text-2xl font-bold text-gradient font-cyber">{step}</span>
          </div>
        </div>
        {!isLast && (
          <div className="w-px h-full mt-4 bg-gradient-to-b from-cyber-blue/50 to-transparent" />
        )}
      </div>
      
      {/* 内容 */}
      <div className="flex-1 pb-10">
        <div className="cyber-card p-5 hover:border-cyber-blue/30 transition-colors">
          <h3 className="text-xl font-semibold mb-2 text-white font-cyber">{title}</h3>
          <p className="text-gray-400">{desc}</p>
        </div>
      </div>
    </div>
  )
}

export default function Home() {
  return (
    <div className="relative">
      <StarField />

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden">
        {/* 背景装饰 */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyber-blue/10 rounded-full blur-3xl animate-pulse-slow" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyber-purple/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }} />
        </div>

        <div className="text-center max-w-5xl mx-auto relative z-10">
          {/* 状态指示器 */}
          <div className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-cyber-dark/80 border border-cyber-blue/30 mb-10 backdrop-blur-sm">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyber-green opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-cyber-green"></span>
            </span>
            <span className="text-cyber-blue text-sm font-mono tracking-wider">SYSTEM ONLINE</span>
            <span className="text-gray-600">|</span>
            <span className="text-gray-400 text-sm font-mono">v1.0.0</span>
          </div>

          {/* 主标题 */}
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold mb-8 font-cyber leading-tight">
            <span className="block text-white mb-2">探索你的</span>
            <span className="block glitch text-gradient-animated" data-text="数字人格">
              数字人格
            </span>
          </h1>

          {/* 副标题 */}
          <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto leading-relaxed">
            基于出生信息的赛博科技星盘分析
            <br />
            <span className="text-cyber-blue font-mono text-base md:text-lg">
              将传统占星学重构为 3D 数据可视化系统
            </span>
          </p>

          {/* CTA 按钮 */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/chart" className="cyber-button text-lg inline-flex items-center gap-2 group">
              <SparklesIcon className="w-5 h-5 group-hover:animate-pulse" />
              <span>立即体验</span>
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <button className="cyber-button-outline text-lg inline-flex items-center gap-2 group">
              <PlayIcon className="w-5 h-5" />
              <span>观看演示</span>
            </button>
          </div>

          {/* 滚动提示 */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2">
            <div className="flex flex-col items-center gap-2 text-gray-500">
              <span className="text-xs font-mono tracking-widest">SCROLL</span>
              <div className="w-6 h-10 rounded-full border-2 border-cyber-blue/30 flex items-start justify-center p-1.5">
                <div className="w-1.5 h-3 bg-cyber-blue rounded-full animate-bounce" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 relative">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyber-purple/10 border border-cyber-purple/20 mb-6">
              <CpuChipIcon className="w-4 h-4 text-cyber-purple" />
              <span className="text-xs text-cyber-purple font-mono tracking-wider">CORE FEATURES</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4 font-cyber">
              <span className="text-gradient">核心功能</span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              融合科技与占星的独特体验，为你呈现前所未有的星盘解读
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard
              icon={CpuChipIcon}
              title="3D 星盘"
              description="Three.js 驱动的交互式 3D 星盘，支持旋转、缩放、点击查看详情"
              gradient="from-cyber-blue to-cyber-cyan"
            />
            <FeatureCard
              icon={ChatBubbleLeftRightIcon}
              title="AI 解读"
              description="基于你的出生信息，AI 生成个性化的赛博风格星盘解读"
              gradient="from-cyber-purple to-cyber-violet"
            />
            <FeatureCard
              icon={ShareIcon}
              title="分享卡片"
              description="一键生成精美分享卡片，展示你的核心人格特征"
              gradient="from-cyber-pink to-cyber-magenta"
            />
            <FeatureCard
              icon={SparklesIcon}
              title="星盘存档"
              description="登录后可保存多个星盘，随时查看历史记录"
              gradient="from-cyber-green to-cyber-cyan"
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-4 relative bg-cyber-dark/30">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyber-blue/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyber-blue/20 to-transparent" />
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyber-blue/10 border border-cyber-blue/20 mb-6">
              <span className="text-xs text-cyber-blue font-mono tracking-wider">HOW IT WORKS</span>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4 font-cyber">
              <span className="text-gradient">使用流程</span>
            </h2>
          </div>

          <div className="relative">
            {[
              {
                step: '01',
                title: '输入出生信息',
                desc: '填写你的出生日期、时间和地点，系统将自动计算星盘数据',
              },
              {
                step: '02',
                title: '生成 3D 星盘',
                desc: '观看你的专属星盘在 3D 空间中呈现，行星位置一目了然',
              },
              {
                step: '03',
                title: '获取 AI 解读',
                desc: 'AI 以赛博科技风格解读你的星盘，分析各个维度的特征',
              },
              {
                step: '04',
                title: '分享与存档',
                desc: '登录后可保存星盘，生成分享卡片发送给好友',
              },
            ].map((item, index) => (
              <ProcessStep
                key={index}
                step={item.step}
                title={item.title}
                desc={item.desc}
                isLast={index === 3}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-cyber-blue/5 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold mb-6 font-cyber">
            准备好探索你的
            <span className="text-gradient"> 数字人格</span>
            了吗？
          </h2>
          <p className="text-gray-400 mb-10 text-lg">
            无需注册，立即生成你的专属星盘
          </p>
          <Link to="/chart" className="cyber-button text-xl inline-flex items-center gap-3 group">
            <SparklesIcon className="w-6 h-6 group-hover:animate-pulse" />
            <span>开始体验</span>
            <ArrowRightIcon className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  )
}
